import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import {
  GlobalNavigationProps,
  HeaderCtaButtonLink,
  HeaderCtaButtons,
  HeaderProps,
  NAV_ITEM_COLOR,
  UtilityNavigationMobileProps,
} from '@ahc-digital/component-library';
import {
  N01HeaderProps,
  N01MainGlobalNavigationItem,
  N01MainGlobalNavigationSection,
  N02UtilityNavigationItem,
  N02UtilityNavigationSection,
} from './N01Header.types';
import {
  N01_GLOBAL_NAV_SECTION_TEMPLATE_ID,
  N01_UTILITY_NAV_SECTION_TEMPLATE_ID,
} from 'src/shared/static';
import { useI18n } from 'next-localization';
import { parseDroplinkParam } from 'src/shared/shared.service';

export function GetHeaderModel(props: N01HeaderProps | undefined, searchPageUrl: string) {
  const id = props?.params?.RenderingIdentifier;
  const backgroundColor =
    props?.params?.BackgroundColor &&
    parseDroplinkParam(props?.params?.BackgroundColor).value?.value;
  const firstButtonColor =
    props?.params?.FirstButtonColor &&
    parseDroplinkParam(props?.params?.FirstButtonColor).value?.value;
  const secondButtonColor =
    props?.params?.SecondButtonColor &&
    parseDroplinkParam(props?.params?.SecondButtonColor).value?.value;
  const { t } = useI18n() || {};
  const closeText = t('Nav-Close');
  const MenuText = t('Nav-Menu');
  const searchText = t('Nav-Search');
  const logoText = t('Nav-Logo');
  const searchPlaceholderText = t('Search-Placeholder-text');
  const { mainNavigation, utilityNavigation } = props?.fields?.data?.header ?? {};
  const navItems = BuildNavigationItems(mainNavigation?.targetItem?.children?.results);

  // get window.location information for Espanol link
  const pathName = usePathname();
  const [currentPageUrl, setCurrentPageUrl] = useState('');

  useEffect(() => {
    const pageUrl = window.location.pathname + window.location.search;
    setCurrentPageUrl(pageUrl);
  }, [pathName]);

  const utilityNavItems = BuildUtilityNavigationItems(
    utilityNavigation?.targetItem?.children?.results,
    currentPageUrl
  );
  const ctaButtonsList = [];
  if (utilityNavigation?.targetItem?.cTAButtonLink?.jsonValue?.value?.href !== '') {
    ctaButtonsList?.push({
      link: (
        <a
          href={utilityNavigation?.targetItem?.cTAButtonLink?.jsonValue?.value?.href}
          target={utilityNavigation?.targetItem?.cTAButtonLink?.jsonValue?.value?.target}
        >
          {utilityNavigation?.targetItem?.cTAButtonLink?.text}
        </a>
      ),
      color: firstButtonColor,
    } as HeaderCtaButtonLink);
  }
  if (utilityNavigation?.targetItem?.cTAButtonLink2?.jsonValue?.value?.href !== '') {
    ctaButtonsList?.push({
      link: (
        <a
          href={utilityNavigation?.targetItem?.cTAButtonLink2?.jsonValue?.value?.href}
          target={utilityNavigation?.targetItem?.cTAButtonLink2?.jsonValue?.value?.target}
        >
          {utilityNavigation?.targetItem?.cTAButtonLink2?.text}
        </a>
      ),
      color: secondButtonColor,
    } as HeaderCtaButtonLink);
  }

  const globalNavigation = {
    logoUrl: mainNavigation?.targetItem?.logoUrl?.jsonValue?.value?.href || '',
    logoImage: (
      <img
        src={mainNavigation?.targetItem?.logo?.jsonValue?.value?.src}
        alt={mainNavigation?.targetItem?.logo?.jsonValue?.value?.alt}
        width={mainNavigation?.targetItem?.logo?.width ?? ''}
        height={mainNavigation?.targetItem?.logo?.height ?? ''}
      />
    ),
    logoImageInverse: (
      <img
        src={mainNavigation?.targetItem?.logoImageInverse?.jsonValue?.value?.src}
        alt={mainNavigation?.targetItem?.logoImageInverse?.jsonValue?.value?.alt}
        width={mainNavigation?.targetItem?.logoImageInverse?.width ?? ''}
        height={mainNavigation?.targetItem?.logoImageInverse?.height ?? ''}
      />
    ),
    logoTitle: mainNavigation?.targetItem?.logoTitle?.value || '',
    backgroundColor: (backgroundColor as GlobalNavigationProps['backgroundColor']) || 'white',
    ariaLabel: 'Main Navigation',
    navigationItems: navItems,
    mobileMenuTriggerOpenLabel: MenuText,
    mobileMenuTriggerCloseLabel: closeText,
    searchLabel: searchText,
    logoLabel: logoText,
    searchPlaceholder: searchPlaceholderText,
    searchUrl: searchPageUrl,
    searchColor: mainNavigation?.targetItem?.searchBarColor?.targetItem?.value
      ?.value as NAV_ITEM_COLOR,
    mobileCallLink: !!mainNavigation?.targetItem?.mobileSupportLink?.jsonValue?.value?.href ? (
      <a
        href={mainNavigation?.targetItem?.mobileSupportLink?.jsonValue?.value?.href}
        target={mainNavigation?.targetItem?.mobileSupportLink?.jsonValue?.value?.target}
      >
        {mainNavigation?.targetItem?.mobileSupportLink?.jsonValue?.value?.text}
      </a>
    ) : undefined,
  };

  const utilNav = {
    ariaLabel: 'Utility Navigation',
    navigationItems: utilityNavItems,
    ctaButtons: ctaButtonsList as HeaderCtaButtons,
  };

  const featuredUtilityCtaNumber =
    utilityNavigation?.targetItem?.highlightedMobileCTA?.value !== 'neither'
      ? (parseInt(
          utilityNavigation?.targetItem?.highlightedMobileCTA?.value || ''
        ) as HeaderProps['featuredUtilityCtaNumber'])
      : undefined;

  return {
    id,
    globalNavigation,
    utilityNavigation: utilityNavigation?.targetItem ? utilNav : undefined,
    featuredUtilityCtaNumber,
  };
}

function BuildNavigationItems(
  data?: (N01MainGlobalNavigationItem | N01MainGlobalNavigationSection)[]
): GlobalNavigationProps['navigationItems'] {
  const navigationItems: GlobalNavigationProps['navigationItems'] = [];
  data?.map((child) => {
    // if child is of type N01-Global Navigation Section
    if (child?.template?.id === N01_GLOBAL_NAV_SECTION_TEMPLATE_ID) {
      const data = child as N01MainGlobalNavigationSection;
      const navItems = data?.children?.results as N01MainGlobalNavigationItem[];
      navigationItems?.push({
        type: 'dropdown',
        title: data?.title?.value || '',
        color: data?.coloredBarClass?.targetItem?.value?.value as NAV_ITEM_COLOR,
        links: navItems?.map((item) => {
          return {
            link: (
              <a
                href={item?.link?.jsonValue?.value?.href}
                target={item?.link?.jsonValue?.value?.target}
              >
                {item?.link?.text}
              </a>
            ),
            mobileLink: (
              <a
                href={item?.link?.jsonValue?.value?.href}
                target={item?.link?.jsonValue?.value?.target}
              >
                {item?.mobileText?.value}
              </a>
            ),
          };
        }),
      });
    } else {
      // if child is of type N01-Global Navigation Item
      const data = child as N01MainGlobalNavigationItem;
      navigationItems?.push({
        type: 'link',
        color: data?.coloredBarClass?.targetItem?.value?.value as NAV_ITEM_COLOR,
        link: (
          <a
            href={data?.link?.jsonValue?.value?.href}
            target={data?.link?.jsonValue?.value?.target}
          >
            {data?.link?.text}
          </a>
        ),
      });
    }
  });

  return navigationItems;
}

function BuildUtilityNavigationItems(
  data?: (N02UtilityNavigationItem | N02UtilityNavigationSection)[],
  pageUrl?: string
): UtilityNavigationMobileProps['navigationItems'] {
  const navigationItems: UtilityNavigationMobileProps['navigationItems'] = [];
  data?.map((child) => {
    // if child is of type N02 - Utility Navigation Item Section
    if (child?.template?.id === N01_UTILITY_NAV_SECTION_TEMPLATE_ID) {
      const data = child as N02UtilityNavigationSection;
      const navItems = data?.children?.results as N02UtilityNavigationItem[];
      navigationItems?.push({
        type: 'dropdown',
        title: data?.title?.value || '',
        links: navItems?.map((item) => {
          return {
            link: (
              <a
                href={item?.link?.jsonValue?.value?.href}
                target={item?.link?.jsonValue?.value?.target}
              >
                {item?.link?.text}
              </a>
            ),
            mobileLink: (
              <a
                href={item?.link?.jsonValue?.value?.href}
                target={item?.link?.jsonValue?.value?.target}
              >
                {item?.mobileText?.value}
              </a>
            ),
          };
        }),
      });
    } else {
      // if child is of type N02 - Utility Navigatin Item
      const data = child as N02UtilityNavigationItem;
      let url = data?.link?.jsonValue?.value?.href || '';

      // append window.location information to current link if "append-class" is present
      if (data?.link?.jsonValue?.value?.class === 'append-path') {
        url = url.endsWith('/') ? url.substring(0, url.length - 1) : url;
        url += pageUrl;
      }

      navigationItems?.push({
        type: 'link',
        link: (
          <a href={url} target={data?.link?.jsonValue?.value?.target}>
            {data?.link?.text}
          </a>
        ),
      });
    }
  });
  return navigationItems;
}
